import { ButtonDestructive, ButtonPrimary, ButtonStatus, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { InputOutlined } from "@nova/ds-component-input"
import { HTMLAttributes, useCallback, useEffect, useRef, useState } from "react"
import { usePilotTrackingConnectedAccountMutation } from "../usePilotTrackingConnectedAccountMutation"
import * as Icons from "@nova/component-icons"
import {
    ConfirmationModalProps,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { ConnectedAccountDeleteDjiModal } from "../delete/ConnectedAccountDeleteDjiModal"
import { IntegrationType, NonSensitiveFlightRecordConnectedAccount } from "@nova/schemas"

export const ConnectedAccountEditDjiModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback: _,
    isPerformingAsyncCancelCallback: __,
    account,
    onSave,
    onDelete,
    ...rest
}: ConfirmationModalProps & {
    account: NonSensitiveFlightRecordConnectedAccount<IntegrationType.dji>
    onSave: (id: string, password: string) => Promise<void>
    onDelete: () => Promise<void>
} & HTMLAttributes<HTMLDivElement>) => {
    const [password, setPassword] = useState("")
    const passwordRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        passwordRef.current?.focus()
    }, [])

    const { trigger, isLoading } = usePilotTrackingConnectedAccountMutation({
        key: "dji-connection",
        confirm,
        cancel,
        mutate: useCallback(async () => {
            await onSave(account.id, password)
        }, [onSave, account.id, password]),
    })

    const confirmationModal = useConfirmationModalContext()
    const showDeleteModal = async () => {
        await confirmationModal.confirm({
            RenderComponent: (props) => (
                <ConnectedAccountDeleteDjiModal {...props} account={account} onDelete={onDelete} />
            ),
        })
    }

    return (
        <HighPriorityNovaModalContainer
            {...rest}
            title="Edit Account"
            closeModal={cancel}
            className="w-[28rem]"
            bottomBar={
                <div className="flex w-full gap-2">
                    <ButtonDestructive
                        title="Disconnect"
                        LeftIcon={Icons.LinkOff}
                        className="mr-auto"
                        onClick={() => showDeleteModal()}
                    />
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonPrimary
                        title="Save"
                        onClick={() => trigger()}
                        status={isLoading ? ButtonStatus.Loading : ButtonStatus.Normal}
                        disabled={isLoading}
                    />
                </div>
            }
        >
            <form
                className="flex flex-col gap-4 mt-4"
                onSubmit={(e) => {
                    e.preventDefault()
                    trigger()
                }}
            >
                <InputOutlined
                    label="DJI Email"
                    autoComplete="off"
                    value={account.accountId}
                    disabled
                />

                {/* This uses type:text and WebkitTextSecurity to prevent the browser
                from prompting the user to save the credentials. */}
                <InputOutlined
                    ref={passwordRef}
                    placeholder="Password"
                    label="DJI Password"
                    type="text"
                    autoComplete="off"
                    style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <button type="submit" className="hidden" aria-hidden="true" />
            </form>
        </HighPriorityNovaModalContainer>
    )
}
